package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedUnitReport
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.unidadeKeyOf
import br.com.vvrefeicoes.portal.domain.repository.ConsolidatedDetailedRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import kotlin.math.abs

data class CategoryDifference(
    val categoryId: String,
    val title: StringResource,
    val realCents: Long,
    val plannedCents: Long,
) {
    val differenceCents: Long
        get() = realCents - plannedCents
}

class ConsolidatedDetailedController(
    private val repository: ConsolidatedDetailedRepository,
    private val orgChartRepository: OrgChartRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)
        private set

    var selection by mutableStateOf(PeriodSelection.current(dateTimeProvider.currentPeriod()))
        private set

    var reports by mutableStateOf<List<ConsolidatedUnitReport>>(emptyList())
        private set

    val totalRealCents: Long
        get() = reports.sumOf { it.realTotalCents }

    val totalPlannedCents: Long
        get() = reports.sumOf { it.plannedTotalCents }

    val differenceCents: Long
        get() = totalRealCents - totalPlannedCents

    val executionPercent: Double
        get() = if (totalPlannedCents == 0L) 0.0 else totalRealCents.toDouble() / totalPlannedCents * 100

    val categoryDifferences: List<CategoryDifference>
        get() =
            reports
                .flatMap { it.categories }
                .groupBy { it.categoryId }
                .map { (id, categories) ->
                    CategoryDifference(
                        categoryId = id,
                        title = categories.first().title,
                        realCents = categories.sumOf { it.realCents },
                        plannedCents = categories.sumOf { it.plannedCents },
                    )
                }.sortedByDescending { abs(it.differenceCents) }

    private var started = false
    private var unitFilter: CompanyRef? = null
    private var managerFilter: String? = null
    private var directorFilter: String? = null
    private var authorizedUnits: List<CompanyRef> = emptyList()
    private var orgChartData: OrgChartData? = null

    fun init() {
        if (started) return
        started = true
        scope.launch {
            loadError.loadGuarded { load() }
            loading = false
        }
    }

    fun applyFilter(
        unit: CompanyRef?,
        manager: String?,
        director: String?,
        authorizedUnits: List<CompanyRef>,
    ) {
        if (unit == unitFilter && manager == managerFilter && director == directorFilter && authorizedUnits == this.authorizedUnits) return
        unitFilter = unit
        managerFilter = manager
        directorFilter = director
        this.authorizedUnits = authorizedUnits
        scope.launch { loadError.loadGuarded { load() } }
    }

    fun applyPeriod(newSelection: PeriodSelection) {
        if (newSelection == selection) return
        selection = newSelection
        scope.launch { loadError.loadGuarded { load() } }
    }

    private suspend fun load() {
        val data =
            orgChartData ?: orgChartRepository
                .loadInitial()
                .currentVersion.data
                .also { orgChartData = it }
        val unitId = unitFilter?.let { data.unidadeKeyOf(it) }
        reports = repository.load(selection, unitId, managerFilter, directorFilter)
    }
}

@Composable
fun rememberConsolidatedDetailedController(): ConsolidatedDetailedController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        ConsolidatedDetailedController(
            repository = appGraph.consolidatedDetailedRepository,
            orgChartRepository = appGraph.orgChartRepository,
            scope = scope,
            dateTimeProvider = appGraph.dateTimeProvider,
            loadError = appGraph.loadError,
        )
    }
}
